import React, { useState, useEffect } from 'react';
import { getProducts } from '@/services/api';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { ShoppingCart, ChevronDown, ChevronUp } from 'lucide-react';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('');
  const { addToCart } = useCart();
  const { isAuthenticated, isAdmin } = useAuth();

  // Category structure
  const categories = {
    'France': {
      subcategories: ['Bordeaux', 'Loire Valley', 'Burgundy', 'Rhones', 'Provence', 'Languedoc', 'Champagne']
    },
    'Italy': {
      subcategories: ['Piedmont', 'Veneto', 'Trentino-Alto Adige', 'Friuli-Venezia Giulia', 'Emilia-Romagna', 'Tuscany', 'Abruzzo', 'Puglia']
    },
    'Spirits': {
      subcategories: ['Vodka', 'Gin', 'Tequila', 'Mezcal', 'Whisky', 'Bourbon']
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, selectedSubcategory]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (selectedCategory) {
        if (selectedCategory === 'Spirits') {
          filters.country = 'Spirits';
          filters.product_type = 'spirits';
          if (selectedSubcategory) {
            filters.region = selectedSubcategory;
          }
        } else {
          filters.country = selectedCategory;
          if (selectedSubcategory) {
            filters.region = selectedSubcategory;
          }
        }
      }
      
      const data = await getProducts(filters);
      setProducts(data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
  };

  const handleCategoryClick = (category) => {
    if (expandedCategory === category) {
      setExpandedCategory('');
      setSelectedCategory('');
      setSelectedSubcategory('');
    } else {
      setExpandedCategory(category);
      setSelectedCategory(category);
      setSelectedSubcategory('');
    }
  };

  const handleSubcategoryClick = (subcategory) => {
    setSelectedSubcategory(subcategory);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    setExpandedCategory('');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="products-page">
      {/* Hero Section */}
      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-light text-gray-900 mb-2">Our Product Catalog</h1>
          <p className="text-gray-600 font-light">Premium wines and spirits for your business</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Category Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-light text-gray-900">Categories</h2>
                {(selectedCategory || selectedSubcategory) && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-600 hover:text-gray-900 font-light"
                  >
                    Clear
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {Object.keys(categories).map((category) => (
                  <div key={category}>
                    <button
                      onClick={() => handleCategoryClick(category)}
                      className={`w-full text-left px-4 py-3 flex items-center justify-between transition ${
                        selectedCategory === category
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-50 text-gray-900 hover:bg-gray-100'
                      }`}
                      data-testid={`category-${category}`}
                    >
                      <span className="font-light">{category}</span>
                      {categories[category].subcategories.length > 0 && (
                        expandedCategory === category ? <ChevronUp size={18} /> : <ChevronDown size={18} />
                      )}
                    </button>

                    {/* Subcategories */}
                    {expandedCategory === category && categories[category].subcategories.length > 0 && (
                      <div className="ml-4 mt-2 space-y-1">
                        {categories[category].subcategories.map((subcategory) => (
                          <button
                            key={subcategory}
                            onClick={() => handleSubcategoryClick(subcategory)}
                            className={`w-full text-left px-4 py-2 text-sm transition ${
                              selectedSubcategory === subcategory
                                ? 'bg-gray-200 text-gray-900 font-normal'
                                : 'text-gray-700 hover:bg-gray-100 font-light'
                            }`}
                            data-testid={`subcategory-${subcategory}`}
                          >
                            {subcategory}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {/* Selected Category Display */}
            {(selectedCategory || selectedSubcategory) && (
              <div className="mb-6">
                <h2 className="text-2xl font-light text-gray-900">
                  {selectedSubcategory || selectedCategory}
                </h2>
                <p className="text-gray-600 font-light text-sm mt-1">
                  {selectedSubcategory ? `${selectedCategory} - ${selectedSubcategory}` : selectedCategory}
                </p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12" data-testid="loading-spinner">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                <p className="mt-4 text-gray-600 font-light">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12 bg-white border border-gray-200 p-12" data-testid="no-products">
                <p className="text-gray-600 text-lg font-light">
                  {selectedCategory || selectedSubcategory 
                    ? 'No products available in this category yet.'
                    : 'Please select a category to view products.'}
                </p>
                <p className="text-gray-500 text-sm font-light mt-2">
                  Products will be added by the admin.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="products-grid">
                {products.map((product) => (
                  <div key={product.id} className="bg-white border border-gray-200 overflow-hidden hover:border-gray-400 transition" data-testid={`product-card-${product.id}`}>
                    <div className="aspect-w-1 aspect-h-1 bg-gray-100">
                      <img
                        src={product.image_url || 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=500'}
                        alt={product.name}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-light text-lg text-gray-900 mb-1" data-testid={`product-name-${product.id}`}>{product.name}</h3>
                      <p className="text-sm text-gray-600 font-light mb-2">{product.region}, {product.country}</p>
                      <p className="text-sm text-gray-500 font-light mb-3 line-clamp-2">{product.description}</p>
                      
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs text-gray-500 font-light">{product.volume}</span>
                        <span className="text-sm text-gray-600 font-light">Stock: {product.stock}</span>
                      </div>

                      {!isAdmin && isAuthenticated && (
                        <div className="pt-3 border-t border-gray-200">
                          <button
                            onClick={() => handleAddToCart(product)}
                            disabled={product.stock === 0}
                            data-testid={`add-to-cart-btn-${product.id}`}
                            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed font-light"
                          >
                            <ShoppingCart size={18} />
                            <span>Add to Cart</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
